import { approxEqual, approxZero, EPSILON } from './epsilon';
import { type MutVec2, type Vec2 } from './Vec2';

/**
 * A 2D affine transform.
 *
 * Stored as the six meaningful components of the 3×3 matrix
 *
 * ```
 *   | a  c  e |
 *   | b  d  f |
 *   | 0  0  1 |
 * ```
 *
 * The naming and ordering deliberately match `CanvasRenderingContext2D.setTransform`
 * and `DOMMatrix`, so the camera transform can be handed to the canvas with no
 * conversion step: `ctx.setTransform(m.a, m.b, m.c, m.d, m.e, m.f)`. Storing a full
 * nine-element array would mean rebuilding that argument list every frame.
 */
export interface Mat3 {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly e: number;
  readonly f: number;
}

export interface MutMat3 {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export const IDENTITY: Mat3 = Object.freeze({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 });

function writeMat(
  out: MutMat3 | undefined,
  a: number,
  b: number,
  c: number,
  d: number,
  e: number,
  f: number
): Mat3 {
  if (out === undefined) return { a, b, c, d, e, f };
  out.a = a;
  out.b = b;
  out.c = c;
  out.d = d;
  out.e = e;
  out.f = f;
  return out;
}

export function mat3(a: number, b: number, c: number, d: number, e: number, f: number): Mat3 {
  return { a, b, c, d, e, f };
}

export function translation(tx: number, ty: number, out?: MutMat3): Mat3 {
  return writeMat(out, 1, 0, 0, 1, tx, ty);
}

export function scaling(sx: number, sy: number = sx, out?: MutMat3): Mat3 {
  return writeMat(out, sx, 0, 0, sy, 0, 0);
}

export function rotation(radians: number, out?: MutMat3): Mat3 {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return writeMat(out, cos, sin, -sin, cos, 0, 0);
}

/** Rotation about an arbitrary point — the form every "rotate selection" gesture needs. */
export function rotationAround(center: Vec2, radians: number, out?: MutMat3): Mat3 {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return writeMat(
    out,
    cos,
    sin,
    -sin,
    cos,
    center.x - center.x * cos + center.y * sin,
    center.y - center.x * sin - center.y * cos
  );
}

export function scalingAround(center: Vec2, sx: number, sy: number = sx, out?: MutMat3): Mat3 {
  return writeMat(out, sx, 0, 0, sy, center.x - center.x * sx, center.y - center.y * sy);
}

/** Reflection across the line through `origin` with direction `direction`. */
export function reflection(origin: Vec2, direction: Vec2, out?: MutMat3): Mat3 {
  const len = Math.hypot(direction.x, direction.y);
  if (len <= EPSILON) return writeMat(out, 1, 0, 0, 1, 0, 0);
  const ux = direction.x / len;
  const uy = direction.y / len;

  const a = ux * ux - uy * uy;
  const b = 2 * ux * uy;
  // Reflect the origin offset back so the line stays fixed.
  return writeMat(
    out,
    a,
    b,
    b,
    -a,
    origin.x - a * origin.x - b * origin.y,
    origin.y - b * origin.x + a * origin.y
  );
}

/**
 * Composition: applies `second` after `first`.
 *
 * Equivalent to the matrix product `second × first`. The argument order reads in the
 * order the transforms are applied, which is the opposite of mathematical notation and
 * the source of most transform bugs — hence the explicit names.
 */
export function compose(first: Mat3, second: Mat3, out?: MutMat3): Mat3 {
  return writeMat(
    out,
    second.a * first.a + second.c * first.b,
    second.b * first.a + second.d * first.b,
    second.a * first.c + second.c * first.d,
    second.b * first.c + second.d * first.d,
    second.a * first.e + second.c * first.f + second.e,
    second.b * first.e + second.d * first.f + second.f
  );
}

export function determinant(m: Mat3): number {
  return m.a * m.d - m.b * m.c;
}

/** Returns `null` for a singular (non-invertible) transform rather than producing `NaN`. */
export function invert(m: Mat3, out?: MutMat3): Mat3 | null {
  const det = determinant(m);
  if (approxZero(det, EPSILON)) return null;
  const invDet = 1 / det;
  return writeMat(
    out,
    m.d * invDet,
    -m.b * invDet,
    -m.c * invDet,
    m.a * invDet,
    (m.c * m.f - m.d * m.e) * invDet,
    (m.b * m.e - m.a * m.f) * invDet
  );
}

/** Transforms a point — translation applies. */
export function transformPoint(m: Mat3, p: Vec2, out?: MutVec2): Vec2 {
  const x = m.a * p.x + m.c * p.y + m.e;
  const y = m.b * p.x + m.d * p.y + m.f;
  if (out === undefined) return { x, y };
  out.x = x;
  out.y = y;
  return out;
}

/**
 * Transforms a direction — translation is **not** applied.
 *
 * Use this for anything that represents a difference rather than a position: line
 * directions, velocity, drag deltas. Passing a direction through `transformPoint` is a
 * classic and hard-to-spot error.
 */
export function transformVector(m: Mat3, v: Vec2, out?: MutVec2): Vec2 {
  const x = m.a * v.x + m.c * v.y;
  const y = m.b * v.x + m.d * v.y;
  if (out === undefined) return { x, y };
  out.x = x;
  out.y = y;
  return out;
}

/**
 * Uniform scale factor of the transform.
 *
 * Derived from `sqrt(|det|)`, which is the factor by which areas change. For a
 * non-uniform or sheared transform this is the geometric mean of the axis scales —
 * the right single number for converting a screen-pixel tolerance into world units.
 */
export function uniformScale(m: Mat3): number {
  return Math.sqrt(Math.abs(determinant(m)));
}

/** True when the transform preserves angles (rotation, uniform scale, translation, reflection). */
export function isConformal(m: Mat3, epsilon: number = EPSILON): boolean {
  return (
    approxZero(m.a * m.c + m.b * m.d, epsilon) &&
    approxEqual(m.a * m.a + m.b * m.b, m.c * m.c + m.d * m.d, epsilon)
  );
}

/** True when the transform flips orientation (contains an odd number of reflections). */
export function isMirrored(m: Mat3): boolean {
  return determinant(m) < 0;
}

export function equals(x: Mat3, y: Mat3, epsilon: number = EPSILON): boolean {
  return (
    approxEqual(x.a, y.a, epsilon) &&
    approxEqual(x.b, y.b, epsilon) &&
    approxEqual(x.c, y.c, epsilon) &&
    approxEqual(x.d, y.d, epsilon) &&
    approxEqual(x.e, y.e, epsilon) &&
    approxEqual(x.f, y.f, epsilon)
  );
}
